<template>
  <ChartBox
    class="management-benchmarks"
    :title="tableInfo.title"
    :description="tableInfo.description"
    fullwidth
  >
    <SkeletonTable
      v-if="isLoading"
      :count-table-column="5"
      :count-table-row="10"
    />

    <ErrorDefault
      :show="hasError"
      @onClickButton="getBenchmarksList()"
    />

    <div v-if="!isLoading && !hasError">
      <v-card-title>
        <v-text-field
          v-model="table.search"
          label="Buscar"
          class="mx-4"
          prepend-icon="mdi-magnify"
        />

        <div>
          <v-icon
            class="mx-8"
            @click="getBenchmarksList"
          >
            mdi-refresh
          </v-icon>
        </div>
      </v-card-title>

      <v-data-table
        :headers="table.headers"
        :items="benchmarksList"
        :search="table.search"
      >
        <template v-slot:item.createdDate="{ item }">
          {{ item.createdDateFormatted }}
        </template>

        <template v-slot:item.url="{ item }">
          <a
            v-bind="item.accessLink"
          >
            {{ item.url.slice(0, 30) + '...' }}
          </a>
        </template>

        <template v-slot:item.freePlan="{ item }">
          <v-chip
            outlined
            filter
          >
            {{ item.freePlan ? 'Sim' : 'Não' }}
          </v-chip>
        </template>

        <template v-slot:item.expiryDate="{ item }">
          <v-chip
            v-if="item.expired"
            outlined
            filter
            color="red"
          >
            {{ $t('Setup.benchmarksEdit.chipExpiredIn', { msg: item.expiryDateFormatted }) }}
          </v-chip>

          <span v-if="item.expiryDateFormatted && !item.expired">
            {{ item.expiryDateFormatted }}
          </span>

          <span v-if="!item.expiryDateFormatted && !item.expired">-</span>
        </template>

        <template v-slot:item.action="{ item }">
          <v-icon
            small
            class="mr-2"
            @click="editItem(item)"
          >
            mdi-pencil
          </v-icon>
        </template>

        <template v-slot:top>
          <v-dialog
            v-model="dialog"
            max-width="400px"
          >
            <v-card>
              <v-card-title class="dialog-title">
                {{ $t('Setup.benchmarksEdit.editFreeTrial') }}
              </v-card-title>

              <v-card-text>
                {{ $t('Setup.benchmarksEdit.confirmFreeTrial') }}

                <v-radio-group
                  v-model="editedItem.freePlan"
                  row
                >
                  <v-radio
                    label="Não"
                    :value="false"
                  />
                  <v-radio
                    label="Sim"
                    :value="true"
                  />
                </v-radio-group>

                <div v-if="editedItem.freePlan">
                  <v-select
                    v-model="selectedPeriod"
                    :items="periods"
                    item-text="name"
                    item-value="id"
                    :label="$t('Setup.benchmarksEdit.selectLabel')"
                    return-object
                  />
                  <span v-if="expiryDateSelected">
                    {{ $t('Setup.benchmarksEdit.expiredDate', { msg: expiryDateSelectedFormatted }) }}
                  </span>
                </div>

                <v-alert
                  v-if="confirmation && !editedItem.freePlan"
                  type="warning"
                >
                  <b>{{ $t('Setup.benchmarksEdit.removePeriod') }}</b>
                </v-alert>

                <v-alert
                  v-if="$store.state.setup.benchmarksUpdate.hasError"
                  colored-border
                  type="error"
                  dense
                  outlined
                  elevation="2"
                >
                  {{ $t('Setup.benchmarksEdit.errorUnexpected') }}
                </v-alert>
              </v-card-text>

              <v-card-actions>
                <v-btn
                  class="ma-2"
                  outlined
                  @click="close"
                >
                  {{ $t('Setup.benchmarksEdit.cancel') }}
                </v-btn>

                <v-btn
                  v-if="editedItem.freePlan || !confirmation"
                  color="primary"
                  @click="save()"
                >
                  {{ $t('Setup.benchmarksEdit.save') }}
                </v-btn>

                <v-btn
                  v-if="!editedItem.freePlan && confirmation"
                  color="primary"
                  @click="save()"
                >
                  {{ $t('Setup.benchmarksEdit.yesConfirm') }}
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </template>
      </v-data-table>
    </div>
  </ChartBox>
</template>

<script>
import { format } from 'date-fns';

import { ChartBox, ErrorDefault } from 'components/base';
import { SkeletonTable } from '@/components/content/tables/SkeletonTable';
import { changePageTitle } from '@/utils';

export default {
  name: 'EditBenchmarks',
  components: {
    SkeletonTable,
    ChartBox,
    ErrorDefault,
  },
  data: () => ({
    table: {
      headers: [
        {
          text: 'Data e hora da criação',
          value: 'createdDate',
          align: 'center',
        },
        {
          text: 'Id do plano',
          value: 'planId',
          align: 'center',
        },
        {
          text: 'Nome do Benchmarks',
          value: 'name',
          align: 'center',
        },
        {
          text: 'User group',
          value: 'planName',
          align: 'center',
        },
        {
          text: 'URL',
          value: 'url',
        },
        {
          text: 'Período gratuito?',
          value: 'freePlan',
          align: 'center',
        },
        {
          text: 'Data expiração',
          value: 'expiryDate',
          align: 'center',
        },
        {
          text: 'Ação',
          value: 'action',
          align: 'center',
        },
      ],
      search: '',
    },
    confirmation: false,
    periods: [
      { id: 7, name: '7 dias' },
      { id: 15, name: '15 dias' },
      { id: 30, name: '30 dias' },
      { id: 45, name: '45 dias' },
      { id: 60, name: '60 dias' },
    ],
    expiryDateSelected: null,
    expiryDateSelectedFormatted: '',
    editedIndex: null,
    editedItem: {},
    dialog: false,
  }),
  computed: {
    isLoading() {
      return this.$store.state.setup.benchmarksList.isLoading;
    },
    hasError() {
      return this.$store.state.setup.benchmarksList.hasError;
    },
    benchmarksList() {
      return this.$store.state.setup.benchmarksList.data.map(item => ({
        ...item,
        accessLink: {
          target: '_blank',
          href: item.url,
        },
        createdDateFormatted: format(new Date(item.createdDate), 'dd/LL/yy - HH:mm'),
        expiryDateFormatted: item.expiryDate ? format(new Date(item.expiryDate), 'dd/LL/yy - HH:mm') : '',
        freePlan: Boolean(item.expiryDate),
      }));
    },
    tableInfo() {
      return {
        title: !this.isLoading ? this.$t('Setup.benchmarksEdit.title') : '',
        description: !this.isLoading ? this.$t('Setup.benchmarksEdit.description') : '',
      };
    },
    selectedPeriod: {
      get() {
        return this.$store.state.setup.activeBenchmarksPeriod;
      },
      set(period) {
        this.selectPeriod(period);
      },
    },
  },
  watch: {
    dialog (val) {
      val || this.close();
    },
  },
  async mounted() {
    changePageTitle('Editar Benchmarks');
    await this.getBenchmarksList();
  },
  methods: {
    async getBenchmarksList() {
      await this.$store.dispatch('setup/getBenchmarksList');
    },
    defaultDialog() {
      this.confirmation = false;
      this.selectPeriod(this.periods[1]);
    },
    selectPeriod(selectedPeriod) {
      this.$store.dispatch('setup/addBenchmarksPeriod', selectedPeriod);

      const dateNow = new Date();
      const expiryDateSelected = dateNow.setDate(dateNow.getDate() + selectedPeriod.id);

      this.expiryDateSelected = expiryDateSelected;
      this.expiryDateSelectedFormatted = format(new Date(expiryDateSelected), 'dd/MM/yyyy');
    },
    editItem(item) {
      this.defaultDialog();
      this.editedIndex = this.benchmarksList.indexOf(item);
      this.editedItem = Object.assign({}, item);
      this.dialog = true;
    },
    close() {
      this.dialog = false;
    },
    async save() {
      let expiryDate = '';

      if (!this.editedItem.freePlan && !this.confirmation) {
        this.confirmation = true;
        return false;
      }

      if (this.editedItem.freePlan) {
        expiryDate = format(new Date(this.expiryDateSelected), 'yyyy-LL-dd');
      }

      const itemUpdated = await this.$store.dispatch('setup/updateBenchmarksById', { expiryDate, id: this.editedItem.id });

      if (!itemUpdated.expiryDate) {
        itemUpdated.expiryDate = '';
      }

      await this.getBenchmarksList();
      this.close();
    },
  },
};
</script>

<style lang="scss" scoped>
.management-benchmarks {
  width: 100%;
}

.v-card {
  .dialog-title {
    font-size: 16px !important;
    font-weight: 600;
    padding-bottom: 25px;
  }

  .v-btn {
    text-transform: none;
    padding: 0 25px;
  }

  .v-alert {
    margin-top: 20px;
  }

  .v-select {
    padding-top: 15px;
    padding-bottom: 10px;
  }
}

::v-deep {
  .ds-chart-box__heading h1 {
    font-size: var(--ds-font-size-xl);
  }
}
</style>
